import { useCallback, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthenticated } from "../../authn";
import {
  fetchSSEStream,
  SSEStreamError,
  SourcePreview,
  StreamPhase,
  isProgressEvent,
  isSourcesFoundEvent,
  isResultEvent,
  isEndEvent,
  isErrorEvent,
} from "../../stream";
import {
  UIStructuredOutput,
  UIThread,
  GetCompletionWithUserMessage200Item,
} from "../../generated/core-api";
import { getUserTimezone } from "./getUserTimezone";

/**
 * Streaming state managed by the hook
 */
export interface StreamingState {
  /** Whether a stream is currently active */
  isStreaming: boolean;
  /** Current progress percentage (0-100) */
  progress: number;
  /** Current processing phase */
  phase: StreamPhase | null;
  /** User-friendly status message */
  message: string;
  /** Sources found during RAG retrieval */
  sources: SourcePreview[];
  /** Final structured result */
  result: UIStructuredOutput | null;
  /** Error message if something went wrong */
  error: string | null;
  /** Timing information from the end event */
  timing: string | null;
}

/**
 * Initial state for streaming
 */
const initialState: StreamingState = {
  isStreaming: false,
  progress: 0,
  phase: null,
  message: "",
  sources: [],
  result: null,
  error: null,
  timing: null,
};

/**
 * Options for configuring the streaming behavior
 */
export interface UseStreamingCompletionOptions {
  /** Timeout in milliseconds (default: 60000) */
  timeout?: number;
  /** Callback when streaming starts */
  onStart?: () => void;
  /** Callback when streaming completes successfully */
  onComplete?: (result: UIStructuredOutput, timing: string) => void;
  /** Callback when an error occurs */
  onError?: (error: string) => void;
}

/**
 * Hook for streaming completions with real-time progress updates.
 *
 * Manages the SSE connection to the streaming completion endpoint and
 * provides state updates for progress, sources, and results.
 *
 * @param workspaceId - The workspace ID
 * @param threadId - The thread ID
 * @param endRef - Ref to the scroll anchor element
 * @param options - Optional configuration
 *
 * @example
 * ```typescript
 * const {
 *   isStreaming,
 *   progress,
 *   phase,
 *   sources,
 *   result,
 *   error,
 *   streamCompletion,
 *   cancel,
 * } = useStreamingCompletion(workspaceId, threadId, endRef);
 *
 * // Submit a message
 * await streamCompletion("What are the payroll deadlines?");
 * ```
 */
export function useStreamingCompletion(
  workspaceId: string,
  threadId: string,
  endRef: React.RefObject<HTMLDivElement>,
  options: UseStreamingCompletionOptions = {}
) {
  const { timeout = 60000, onStart, onComplete, onError } = options;

  const authCtx = useAuthenticated();
  const queryClient = useQueryClient();
  const abortControllerRef = useRef<AbortController | null>(null);
  const resultRef = useRef<UIStructuredOutput | null>(null);

  const [state, setState] = useState<StreamingState>(initialState);

  // Query key for thread data
  const queryKey = useMemo(
    () => ["workspace", workspaceId, "thread", threadId],
    [workspaceId, threadId],
  );

  /**
   * Add messages to the thread cache
   */
  const addMessages = useCallback(
    (messages: GetCompletionWithUserMessage200Item[]) => {
      queryClient.setQueryData<UIThread>(queryKey, (current) =>
        current === undefined
          ? undefined
          : {
              ...current,
              messages: [...current.messages, ...messages],
            }
      );
    },
    [queryKey, queryClient]
  );

  /**
   * Build the streaming endpoint URL
   */
  const getStreamUrl = useCallback(async () => {
    // Import environment dynamically to avoid circular dependencies
    const { environment } = await import("../../environment");
    const env = await environment();
    return `${env.BASE_URL}/api/v1/workspace/${workspaceId}/thread/${threadId}/completion/stream`;
  }, [workspaceId, threadId]);

  /**
   * Cancel an ongoing stream
   */
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setState((prev) => ({ ...prev, isStreaming: false }));
  }, []);

  /**
   * Reset state for a new stream
   */
  const resetState = useCallback(() => {
    resultRef.current = null;
    setState({
      ...initialState,
      isStreaming: true,
    });
  }, []);

  /**
   * Stream a completion for the given content
   */
  const streamCompletion = useCallback(
    async (content: string) => {
      // Cancel any existing stream
      cancel();

      // Reset state
      resetState();
      onStart?.();

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      // Add user message optimistically
      const messageTimestamp = new Date().toISOString();
      const userMessage: GetCompletionWithUserMessage200Item = {
        role: "user",
        content,
        timestamp: messageTimestamp,
      };
      addMessages([userMessage]);

      // Scroll to bottom
      endRef.current?.scrollIntoView({ behavior: "smooth" });

      try {
        // Get token and URL
        const token = await authCtx.getAccessTokenSilently();
        const url = await getStreamUrl();

        // Start streaming
        for await (const event of fetchSSEStream(url, {
          token,
          body: { content, user_timezone: getUserTimezone() },
          signal: abortControllerRef.current.signal,
          timeout,
        })) {
          // Handle different event types
          if (isProgressEvent(event)) {
            const payload = event[1];
            setState((prev) => ({
              ...prev,
              progress: payload.progress_percent,
              phase: payload.phase,
              message: payload.message,
            }));
          } else if (isSourcesFoundEvent(event)) {
            const payload = event[1];
            setState((prev) => ({
              ...prev,
              sources: payload.sources,
            }));
          } else if (isResultEvent(event)) {
            const result = event[1];
            resultRef.current = result;
            setState((prev) => ({
              ...prev,
              result,
              progress: 100,
              phase: "complete",
            }));

            // Add assistant message to cache
            const assistantMessage: GetCompletionWithUserMessage200Item = {
              role: "assistant",
              content: result,
              timestamp: new Date().toISOString(),
            };
            addMessages([assistantMessage]);
          } else if (isEndEvent(event)) {
            const timing = event[1];
            setState((prev) => ({
              ...prev,
              isStreaming: false,
              timing,
            }));

            // Call completion callback using ref to avoid stale closure
            if (resultRef.current) {
              onComplete?.(resultRef.current, timing);
            }
          } else if (isErrorEvent(event)) {
            const errorMessage = event[1];
            setState((prev) => ({
              ...prev,
              isStreaming: false,
              error: errorMessage,
            }));
            onError?.(errorMessage);
            break;
          }
        }
      } catch (error) {
        let errorMessage = "An unexpected error occurred";

        if (error instanceof SSEStreamError) {
          if (error.isTimeout) {
            errorMessage =
              "The response took too long. This might be due to a slow connection or a complex query. Please try again.";
          } else if (error.statusCode === 401) {
            errorMessage = "Session expired. Please log in again.";
          } else if (error.statusCode === 404) {
            errorMessage = "Thread not found.";
          } else if (error.statusCode && error.statusCode >= 500) {
            errorMessage =
              "The server encountered an error. Please try again in a moment.";
          } else {
            errorMessage = error.message;
          }
        } else if (error instanceof Error) {
          // Check for abort - user cancelled, not an error
          if (error.name === "AbortError") {
            setState((prev) => ({ ...prev, isStreaming: false }));
            return;
          }
          errorMessage = error.message;
        }

        setState((prev) => ({
          ...prev,
          isStreaming: false,
          error: errorMessage,
        }));
        onError?.(errorMessage);
      } finally {
        abortControllerRef.current = null;
        // Final scroll
        setTimeout(() => {
          endRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    },
    [
      cancel,
      resetState,
      onStart,
      addMessages,
      endRef,
      authCtx,
      getStreamUrl,
      timeout,
      onComplete,
      onError,
    ]
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    streamCompletion,
    cancel,
    clearError,
  };
}
